import {
  useAddressesByParty,
  useCreateAddress,
  useDeleteAddress,
  useUpdateAddress,
} from '@my/directory-api/hooks';
import type {
  Address,
  AddressTypeValue,
  CreateAddressRequest,
  UpdateAddressRequest,
} from '@my/directory-api';
import {
  AddressInlineEditor,
  ConfirmDialog,
  ErrorBanner,
  Loading,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import { COUNTRIES } from '@my/platform/options';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PartyAddressesSectionProps {
  organisationId: string;
  partyId: string;
  /** Primary address id — excluded from this list (managed in basic-info FormSection above). */
  primaryAddressId: string;
  /** Billing address id — excluded from this list (managed in basic-info FormSection above). */
  billingAddressId: string;
}

/**
 * Country options — local copy to keep the component self-contained.
 * Same `${flag} ${name}` format as the rest of the parties feature so
 * dropdowns read identically across the page.
 */
const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));

/** Local draft shape — same fields the AddressInlineEditor reads / writes. */
interface AddressDraft {
  code: string;
  name: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
}

const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  code: '',
  name: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
  coordinatesSource: null,
};

function addressToDraft(a: Address): AddressDraft {
  return {
    code: a.code ?? '',
    name: a.name,
    fullAddress: a.fullAddress,
    city: a.city ?? '',
    postcode: a.postcode ?? '',
    countryIsoCode: a.countryIsoCode,
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
    placeId: a.placeId,
    coordinatesSource: a.coordinatesSource ?? null,
  };
}

/**
 * Card grid + inline editor for the party's *additional* addresses
 * (everything beyond Primary + Billing, which are managed in the
 * basic-info FormSection at the top of the page). New rows default to
 * `addressType: 'Task'` — generic business addresses attached to the
 * party (delivery points, secondary offices, etc).
 *
 * Geolocation gate (lat/lng required) is enforced via the same
 * `addressIsValid` rule as the FormSection's primary/billing editors —
 * operators must verify the address before the green tick lights up.
 */
export function PartyAddressesSection({
  organisationId,
  partyId,
  primaryAddressId,
  billingAddressId,
}: PartyAddressesSectionProps) {
  const { t } = useTranslation(['parties', 'common']);

  const listQuery = useAddressesByParty(organisationId, partyId);
  const createMutation = useCreateAddress();
  const updateMutation = useUpdateAddress();
  const deleteMutation = useDeleteAddress();

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Editor state — `'new'` = drafting a fresh address pinned to the top;
  // a guid = editing the row with that id; `null` = no editor open.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  const [pendingDelete, setPendingDelete] = useState<Address | null>(null);

  const startNew = () => {
    setEditingId('new');
    setDraft(EMPTY_ADDRESS_DRAFT);
  };

  const startEdit = (a: Address) => {
    setEditingId(a.id);
    setDraft(addressToDraft(a));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_ADDRESS_DRAFT);
  };

  // Address is valid for save when name + line1 + 2-letter country are
  // present AND lat/lng are non-null (geolocation verified). Same rule as
  // the FormSection's primary/billing editors.
  const isValid =
    draft.name.trim().length > 0 &&
    draft.fullAddress.trim().length > 0 &&
    draft.placeId.trim().length > 0 &&
    draft.countryIsoCode.length === 2 &&
    draft.latitude != null &&
    draft.longitude != null;

  const handleSave = async () => {
    if (!isValid) return;
    try {
      if (editingId === 'new') {
        const body: CreateAddressRequest = {
          organisationId,
          // Default new additional addresses to Task — generic business
          // address attached to the party. The dedicated Primary +
          // Billing slots are managed elsewhere; nothing else lands here.
          addressType: 'Task' as AddressTypeValue,
          partyId,
          siteId: null,
          code: draft.code.trim() || null,
          name: draft.name.trim(),
          fullAddress: draft.fullAddress.trim(),
          city: draft.city.trim() || null,
          postcode: draft.postcode.trim() || null,
          countryIsoCode: draft.countryIsoCode.toUpperCase(),
          latitude: draft.latitude,
          longitude: draft.longitude,
          coordinatesSource: draft.coordinatesSource ?? null,
          placeId: draft.placeId.trim(),
        };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('parties:addresses.created') });
      } else if (typeof editingId === 'string') {
        const existing = (listQuery.data ?? []).find((a) => a.id === editingId);
        const body: UpdateAddressRequest = {
          addressId: editingId,
          // Echo back the row's existing AddressType — the editor doesn't
          // expose it. Fallback to `Task` for the (unreachable) case
          // where the row vanished mid-edit.
          addressType: existing?.addressType ?? ('Task' as AddressTypeValue),
          status: existing?.status ?? 'Active',
          // Echoed like the two above: this editor shows no tags, and a full
          // replace that omits them clears whatever the Locations screen set.
          serviceTags: existing?.serviceTags ?? null,
          code: draft.code.trim() || null,
          name: draft.name.trim(),
          fullAddress: draft.fullAddress.trim(),
          city: draft.city.trim() || null,
          postcode: draft.postcode.trim() || null,
          countryIsoCode: draft.countryIsoCode.toUpperCase(),
          latitude: draft.latitude,
          longitude: draft.longitude,
          coordinatesSource: draft.coordinatesSource ?? null,
          placeId: draft.placeId.trim(),
        };
        await updateMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('parties:addresses.saved') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:addresses.saveFailed'),
      });
    }
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    try {
      await deleteMutation.mutateAsync({ id: target.id, partyId });
      showToast({ type: 'success', message: t('parties:addresses.deleted') });
      if (editingId === target.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:addresses.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Show every address linked to the party — primary + billing render
  // alongside the additional ones so operators have a single overview
  // of what the party has on file. Primary / billing rows carry purple
  // badges and their edit button is disabled (those are managed in the
  // basic-info FormSection above; the card grid is read-only for them).
  // Sort: primary first, then billing, then everything else by name.
  const visibleAddresses = [...(listQuery.data ?? [])].sort((a, b) => {
    const rank = (id: string) =>
      id === primaryAddressId ? 0 : id === billingAddressId ? 1 : 2;
    const ra = rank(a.id);
    const rb = rank(b.id);
    if (ra !== rb) return ra - rb;
    return (a.name || a.fullAddress).localeCompare(b.name || b.fullAddress, undefined, {
      sensitivity: 'base',
    });
  });

  return (
    <section className="rounded-sm border border-slate-300 bg-white p-3 shadow-sm">
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {t('parties:additionalAddresses.title')}
        </h3>
        {editingId === null && (
          <button
            type="button"
            onClick={startNew}
            disabled={isBusy}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('common:actions.add')}
          </button>
        )}
      </header>

      {listQuery.isPending && <Loading label={t('parties:additionalAddresses.title')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('parties:addresses.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && (
        <div className="space-y-2">
          {/* New-address draft pinned to the top */}
          {editingId === 'new' && (
            <AddressEditorPanel
              draft={draft}
              onChange={setDraft}
              onSave={handleSave}
              onCancel={cancel}
              isBusy={isBusy}
              isValid={isValid}
            />
          )}

          {visibleAddresses.length === 0 && editingId !== 'new' ? (
            <p className="px-3 py-4 text-center text-xs italic text-slate-400">
              {t('parties:addresses.empty')}
            </p>
          ) : (
            visibleAddresses.map((a) =>
              editingId === a.id ? (
                <AddressEditorPanel
                  key={a.id}
                  draft={draft}
                  onChange={setDraft}
                  onSave={handleSave}
                  onCancel={cancel}
                  onDelete={() => setPendingDelete(a)}
                  isBusy={isBusy}
                  isValid={isValid}
                />
              ) : (
                <AddressCard
                  key={a.id}
                  address={a}
                  isPrimary={a.id === primaryAddressId}
                  isBilling={a.id === billingAddressId}
                  rowsLocked={editingId !== null}
                  onEdit={() => startEdit(a)}
                />
              ),
            )
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('parties:addresses.delete.title')}
        message={
          pendingDelete
            ? t('parties:addresses.delete.message', {
                name: pendingDelete.name || pendingDelete.fullAddress,
              })
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

// ── AddressCard ─────────────────────────────────────────────────────────────

function AddressCard({
  address,
  isPrimary,
  isBilling,
  rowsLocked,
  onEdit,
}: {
  address: Address;
  isPrimary: boolean;
  isBilling: boolean;
  rowsLocked: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('parties');
  // One-line summary built from the meaningful address parts only —
  // empties drop out so we don't render leading commas / double-spaces.
  // FullAddress already carries the comma-joined Google-formatted line;
  // fall back to the sidecar fields when (legacy) it's empty.
  const summary =
    address.fullAddress?.trim() ||
    [address.city, address.postcode, address.countryIsoCode].filter(Boolean).join(', ');

  // Primary / Billing rows are read-only here (edit happens in the
  // basic-info FormSection above). Lock state combines the global
  // "another row is being edited" lock with the per-row primary/billing
  // lock for the disabled state on the edit button.
  const isLocked = isPrimary || isBilling;
  const editDisabled = rowsLocked || isLocked;

  return (
    <div
      className={`flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-white p-3 ${
        rowsLocked && !isLocked ? 'opacity-50' : isLocked ? '' : 'hover:bg-slate-50'
      }`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {address.name || address.fullAddress}
          </p>
          {/* Address Code in smaller font next to the name — short
              reference codes (HQ, WH-01) read cleanly as a sub-tag. */}
          {address.code && (
            <span className="text-[10px] font-medium text-slate-400">
              {address.code}
            </span>
          )}
          {/* Read-only badges driven by the party's primaryAddressId /
              billingAddressId pointers. Both can be set on the same row
              when "Same as primary" was on at create time. */}
          {isPrimary && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
              {t('addressBadge.primary')}
            </span>
          )}
          {isBilling && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
              {t('addressBadge.billing')}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-slate-500">{summary}</p>
      </div>
      <button
        type="button"
        aria-label={`Edit ${address.name || address.fullAddress}`}
        disabled={editDisabled}
        onClick={onEdit}
        className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
      >
        <Edit className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── AddressEditorPanel ──────────────────────────────────────────────────────

interface AddressEditorPanelProps {
  draft: AddressDraft;
  onChange: (next: AddressDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden in `new` mode; shown only when editing an existing address. */
  onDelete?: () => void;
  isBusy: boolean;
  isValid: boolean;
}

function AddressEditorPanel({
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  isValid,
}: AddressEditorPanelProps) {
  const { t } = useTranslation('common');

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* Hide the AddressInlineEditor's own summary line + footer — this
          panel sits inside the AdditionalAddresses card grid where the
          parent (this component) already provides Cancel / Save / Delete
          buttons below. The summary line would just be visual noise
          here, and a duplicate Save button would be confusing. */}
      <AddressInlineEditor
        value={draft}
        onChange={onChange}
        countryOptions={COUNTRY_OPTIONS}
        hasCodeField
        hasNameField
        enablePlacesSearch
        showMap
        showSummaryInput={false}
        showFooter={false}
      />

      <div className="flex items-center justify-between gap-2 pt-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('actions.delete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('actions.cancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('actions.save')}
            onClick={onSave}
            disabled={isBusy || !isValid}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
